import { getToken } from "../../../../shared/data/storage/authStorage";
import { API_BASE_URL } from "../../../../config/api";
import type { AllBankAccountsResponseDTO } from "../models/allBankAccountsDto";
import type { WeeklyCashFlowDTO } from "../models/weeklyCashFlowDto";
import type { RecentBankTransactionsResponseDTO } from "../models/recentBankTransactionsDto";
import type { GetRecentBankTransactionsParams } from "../../domain/repositories/CashAndBankRepository";
import type { ReconciliationStatusResponseDTO } from "../models/reconciliationStatusDto";

/**
 * Fetch all bank accounts for the client Cash & Bank view.
 * GET /api/client/cashAndBank/all-accounts
 * Returns bank + fixed deposit accounts for cash distribution; frontend maps types.
 */
export async function getAllAccounts(): Promise<AllBankAccountsResponseDTO> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  // Request the server-side filtered view for the Cash Distribution section.
  const url = `${API_BASE_URL}/api/client/cashAndBank/all-accounts?scope=cash_distribution`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      (err as { message?: string }).message ?? "Failed to fetch bank accounts"
    );
  }

  const json = await res.json();
  if (json.success && json.data) {
    return json.data as AllBankAccountsResponseDTO;
  }
  throw new Error("Invalid response from all-accounts API");
}

export interface GetWeeklyCashFlowParams {
  year?: number;
  month?: number;
}

/**
 * Fetch weekly cash flow for the current (or given) month.
 * GET /api/client/cashAndBank/weekly-cash-flow
 */
export async function getWeeklyCashFlow(
  params?: GetWeeklyCashFlowParams,
): Promise<WeeklyCashFlowDTO> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const searchParams = new URLSearchParams();
  if (params?.year != null) searchParams.set("year", String(params.year));
  if (params?.month != null) searchParams.set("month", String(params.month));
  const qs = searchParams.toString();
  const url = `${API_BASE_URL}/api/client/cashAndBank/weekly-cash-flow${qs ? `?${qs}` : ""}`;

  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      (err as { message?: string }).message ??
        "Failed to fetch weekly cash flow",
    );
  }

  const json = await res.json();
  if (json.success && json.data) {
    return json.data as WeeklyCashFlowDTO;
  }
  throw new Error("Invalid response from weekly-cash-flow API");
}

/**
 * Fetch recent bank transactions (paginated, filterable by status and date range).
 * GET /api/client/cashAndBank/recent-transactions
 */
export async function getRecentBankTransactions(
  params: GetRecentBankTransactionsParams
): Promise<RecentBankTransactionsResponseDTO> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const searchParams = new URLSearchParams();
  if (params.status != null) searchParams.set("status", params.status);
  if (params.period != null) searchParams.set("period", params.period);
  if (params.date_from != null) searchParams.set("date_from", params.date_from);
  if (params.date_to != null) searchParams.set("date_to", params.date_to);
  if (params.page != null) searchParams.set("page", String(params.page));
  if (params.limit != null) searchParams.set("limit", String(params.limit));

  const qs = searchParams.toString();
  const url = `${API_BASE_URL}/api/client/cashAndBank/recent-transactions${qs ? `?${qs}` : ""}`;

  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      (err as { message?: string }).message ??
        "Failed to fetch recent bank transactions"
    );
  }

  const json = await res.json();
  if (json.success && json.data) {
    return json.data as RecentBankTransactionsResponseDTO;
  }
  throw new Error("Invalid response from recent-transactions API");
}

interface ReconciliationStatusApiParams {
  page?: number;
  limit?: number;
}

/**
 * Fetch reconciliation status per bank account (paginated).
 * GET /api/client/cashAndBank/reconciliation-status?page=1&limit=5
 */
export async function getReconciliationStatus(
  params: ReconciliationStatusApiParams
): Promise<ReconciliationStatusResponseDTO> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const searchParams = new URLSearchParams();
  if (params.page != null) searchParams.set("page", String(params.page));
  if (params.limit != null) searchParams.set("limit", String(params.limit));
  const qs = searchParams.toString();
  const url = `${API_BASE_URL}/api/client/cashAndBank/reconciliation-status${qs ? `?${qs}` : ""}`;

  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      (err as { message?: string }).message ??
        "Failed to fetch reconciliation status"
    );
  }

  const json = await res.json();
  if (json.success && json.data) {
    return json.data as ReconciliationStatusResponseDTO;
  }
  throw new Error("Invalid response from reconciliation-status API");
}
